import { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useToast, ToastContainer } from '../../../components/Toast/Toast';
import { spriteApi, type SpriteEntry } from '../../../api/adminApi';
import { HUB_NPC_ROLES } from '../HubMapAdminPage';
import '../admin.css';

const ADMIN_TOKEN = import.meta.env.VITE_ADMIN_TOKEN ?? 'darkage-admin-dev-token';
const PREVIEW = 96;

interface HubConfigLike {
  npcSprites?: Record<string, string | undefined>;
  [key: string]: unknown;
}

/**
 * Preview de UM frame (parado, coluna 0) do spritesheet escolhido — mesmo
 * recorte usado no jogo real (ver NPC_FACING_ROW em CombatGrid.tsx), só que
 * aqui sempre mostra a linha 0 por simplicidade.
 */
function SpritePreview({ sprite }: { sprite?: SpriteEntry }) {
  if (!sprite) {
    return (
      <div style={{
        width: PREVIEW, height: PREVIEW, borderRadius: 8,
        background: 'var(--input-bg)', border: '1px dashed var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-dim)', fontSize: 11, textAlign: 'center', padding: 6,
      }}>
        Sem sprite — marcador mínimo no jogo
      </div>
    );
  }
  const cols = sprite.cols || 3;
  return (
    <div style={{
      width: PREVIEW, height: PREVIEW, overflow: 'hidden', position: 'relative',
      borderRadius: 8, border: '1px solid var(--border)', background: 'var(--input-bg)',
    }}>
      <img
        src={`/sprites/${sprite.filename || sprite.name + '.png'}?v=${Date.now()}`}
        alt={sprite.label}
        style={{
          position: 'absolute',
          top: sprite.offsetY || 0,
          left: sprite.offsetX || 0,
          width: `calc(${PREVIEW}px * ${cols})`,
          height: 'auto',
          imageRendering: 'pixelated',
          transform: `scale(${sprite.scale || 1})`,
          transformOrigin: 'top left',
        }}
      />
    </div>
  );
}

export function NpcConfigPage() {
  const { t } = useTranslation();
  const { messages, toast, remove } = useToast();
  const [config, setConfig] = useState<HubConfigLike | null>(null);
  const [sprites, setSprites] = useState<SpriteEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch('/api/admin/hub/config', { headers: { 'X-Admin-Token': ADMIN_TOKEN } }).then(r => r.json()),
      spriteApi.list(),
    ])
      .then(([cfg, spr]) => {
        // PHP serializa um mapa vazio como `[]` — normaliza para objeto.
        const npcSprites = cfg?.npcSprites && !Array.isArray(cfg.npcSprites) ? cfg.npcSprites : {};
        setConfig({ ...cfg, npcSprites });
        setSprites(spr);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const npcSpriteOptions = sprites.filter(s => s.type === 'npc');
  const byName = new Map(sprites.map(s => [s.name, s]));

  const setRoleSprite = (role: string, spriteName: string) => {
    setConfig(c => ({
      ...(c ?? {}),
      npcSprites: { ...(c?.npcSprites ?? {}), [role]: spriteName || undefined },
    }));
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const res = await fetch('/api/admin/hub/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': ADMIN_TOKEN },
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Configuração de NPCs salva com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Falha ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) return <p style={{ padding: 20 }}>{t('common.loading')}</p>;

  return (
    <>
      <ToastContainer messages={messages} onRemove={remove} />

      <div className="adminHeader">
        <h1>Configuração de NPCs</h1>
        <button className="btn btnPrimary" onClick={handleSave} disabled={saving}>
          {saving ? t('common.saving' as any) : t('common.save')}
        </button>
      </div>

      <div className="adminContent">
        <div className="adminCard" style={{ marginBottom: 20 }}>
          <div style={{ padding: '15px 18px' }}>
            <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: 0 }}>
              Escolha qual sprite cada NPC fixo do Hub usa. Só aparecem sprites do tipo <em>NPC</em>
              — envie um novo em <strong>Sprites</strong> (Atribuir a: NPC) se a lista estiver vazia.
              Sem escolha, o NPC não usa nenhuma arte automática — aparece só com um marcador
              mínimo no jogo até você selecionar um sprite aqui.
            </p>
            {npcSpriteOptions.length === 0 && (
              <p style={{ fontSize: 12, color: '#e0b050', marginTop: 10, marginBottom: 0 }}>
                ⚠ Nenhum sprite do tipo NPC cadastrado ainda.
              </p>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          {HUB_NPC_ROLES.map(({ role, icon, label }) => {
            const chosen = config.npcSprites?.[role] ?? '';
            const selectedSprite = chosen ? byName.get(chosen) : undefined;
            return (
              <div key={role} className="adminCard" style={{ width: 320 }}>
                <div className="adminCardHeader">{icon} {label}</div>
                <div style={{ padding: 18, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <SpritePreview sprite={selectedSprite} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label className="formLabel">Sprite</label>
                    <select
                      className="formInput"
                      value={chosen}
                      onChange={e => setRoleSprite(role, e.target.value)}
                    >
                      <option value="">— Nenhum (marcador mínimo) —</option>
                      {npcSpriteOptions.map(s => (
                        <option key={s.id ?? s.name} value={s.name}>
                          {s.icon} {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
